import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { DropdownSingleSelect, IDropdownSingleSelectProps, Text } from '@opendoor/bricks/core';

import { SelectQuestion } from 'src/lib/flow/FlowQuestion';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';

import QuestionLabel from '../common/QuestionLabel';
import { trackAnswerChange } from './trackAnswerChange';

const getDropdownSelectOptions = (
  options: SelectQuestion['options'],
  min?: number,
  maxValue?: number | string,
): { shouldPullValue: boolean; options: IDropdownSingleSelectProps['options'] | undefined } => {
  let shouldPullValue = false;
  let answerOptions = (options ?? []).map(({ label, value }) => {
    if (typeof value === 'object') {
      shouldPullValue = true;
    }
    return { label, value: typeof value === 'object' ? label : value };
  });

  // This is needed since the Years dropdown dynamically calculates
  // the max value, but that is always returned as a string
  const max = maxValue ?? Number(maxValue);
  if (min !== undefined && max !== undefined) {
    answerOptions = [];
    for (let i = min; i < max; i++) {
      answerOptions.push({ label: `${i}`, value: i });
    }
  }

  return { options: answerOptions, shouldPullValue };
};

export type SelectInputProps = {
  question: SelectQuestion | (Omit<SelectQuestion, 'type'> & { type: 'combo_input' });
  value?: string;
  isLoading?: boolean;
};

const SelectInput: React.FC<SelectInputProps> = ({ question, value, isLoading = false }) => {
  const [message, setMessage] = useState('');
  const { key, placeholder, helperText, label } = question;

  let defaultValue: SelectQuestion['defaultValue'],
    minValue: SelectQuestion['minValue'],
    maxValue: SelectQuestion['maxValue'],
    options: SelectQuestion['options'] = [];

  if (question.type == 'dropdown_select') {
    defaultValue = question.defaultValue;
    minValue = question.minValue;
    maxValue = question.maxValue;
    options = question.options ?? [];
  }
  const dispatch = useDispatch();

  let selected = value ?? defaultValue ?? null;

  if (selected) {
    // for buying power modal we have a dropdown whose values are objects
    // to have this behave properly we need find the matching value object
    // and then use the label as the value.
    // we also do this onChange (see shouldPullValue) so that we map the label
    // back to the original value when needed.
    const selectedOption = options.find(
      ({ value: optValue }) => JSON.stringify(optValue) == JSON.stringify(selected),
    );
    if (typeof selectedOption?.value === 'object') {
      selected = selectedOption.label;
    }
  }

  const { options: selectOptions, shouldPullValue } = getDropdownSelectOptions(
    options,
    minValue,
    maxValue,
  );

  const onChange = (v?: string) => {
    let newValue: SavedAnswer | undefined = v;
    if (newValue) {
      if (shouldPullValue) {
        const opt = options?.find(({ label }) => {
          return label === newValue;
        });
        if (opt) {
          newValue = opt.value;
        }
      }
    }
    dispatch(changeAnswers([{ key, answer: newValue ?? null }]));
    trackAnswerChange(key, newValue);
  };

  return (
    <>
      <DropdownSingleSelect
        aria-labelledby={key}
        name={key}
        aria-haspopup="listbox"
        onChange={onChange}
        isLoading={isLoading}
        placeholder={placeholder}
        options={selectOptions}
        isSearchable
        selectedValue={selected !== null ? [selected] : []}
        aria-describedby="custom-message"
        onFocus={() => setMessage('Add a text to the autocomplete')}
        onBlur={() => setMessage('')}
        title={label ? <QuestionLabel id={key} title={label} unboundedWidth /> : null}
        helperText={helperText}
      />
      {/* Custom Aria Message for SelectInput, The instruction no longer announces to the screenreader. Ensure instructions are present before the user enters the input field */}
      <Text
        id="custom-message"
        aria-live="polite"
        aria-atomic="true"
        position="absolute"
        width="1px"
        height="1px"
        margin="-1px"
        padding={0}
        overflow="hidden"
        __css={{
          clip: 'rect(0, 0, 0, 0)',
        }}
        whiteSpace="nowrap"
        border={0}
      >
        {message}
      </Text>
    </>
  );
};

export default SelectInput;
